<template>
	<div
		ref="select"
		@click.stop="() => {}"
		class="select"
		style="position: relative"
	>
		<ContainerBox
			:border="props.border"
			:border-active="props.borderActive"
			:box-shadow="props.borderBoxShadowDisabled ? 'none' : props.boxShadow"
			:box-shadow-active="
				props.borderBoxShadowDisabled ? 'none' : props.boxShadowActive
			"
			:active="state.dropdownOpen"
			style="transition: box-shadow 100ms ease"
			:borderRadius="props.containerBorderRadius"
		>
			<div
				@click.stop="state.dropdownOpen ? hideDropdown() : displayDropdown()"
				role="button"
				class="main-box d-flex flex-row justify-content-between"
				:class="props.disabled && 'disabled'"
			>
				<input
					:id="props.id"
					ref="input"
					class="autocomplete-input form-control-plaintext"
					type="text"
					style="padding: 16px 0px 16px 16px"
					:value="state.inputValue"
					:placeholder="props.placeholder"
					@click.stop="() => {}"
					@input="handleInput"
					@focus="displayDropdown"
				/>
				<div
					class="d-flex justify-content-center align-items-center"
					style="padding: 16px"
				>
					<ChevronDownSvg />
				</div>
			</div>
		</ContainerBox>

		<!-- dropdown -->
		<ContainerBox
			:height="'initial'"
			:background="'white'"
			v-if="state.dropdownOpen"
			class="selectDropdown"
		>
			<div
				role="button"
				class="select-dropdown-option"
				v-for="(option, option_i) in optionsToShow"
				@click="() => handleSelect(option)"
				:key="option_i"
			>
				<slot :option="option">
					{{ option.label }}
				</slot>
			</div>
		</ContainerBox>
	</div>
</template>

<script lang="ts" setup>
import {
	withDefaults,
	defineProps,
	defineEmits,
	reactive,
	ref,
	computed,
	watch,
} from "vue"
import ContainerBox from "@/components/LoginRegister/common/ContainerBox/v1/ContainerBox.vue"
import ChevronDownSvg from "@/components/LoginRegister/svg/ChevronDownSvg.vue"
import { onClickOutside } from "@vueuse/core"

type SelectValue = string | number | boolean
type SelectOption = {
	value: SelectValue
	label: string
	[key: string]: any
}
type SelectOptionsList = SelectOption[]

interface Props {
	id?: string
	disabled?: boolean
	options: SelectOptionsList
	selectedOption: SelectOption | null | undefined
	placeholder: string
	border?: string
	borderActive?: string
	boxShadow?: string
	boxShadowActive?: string
	borderBoxShadowDisabled?: boolean
	maxResults?: number
	filterFields: string[]
	containerBorderRadius?: string
}
interface Emits {
	(e: "selectClick", option: SelectOption): void
}

// props
const props = withDefaults(defineProps<Props>(), {
	maxResults: 10,
})

// emit
const emit = defineEmits<Emits>()

// refs
const select = ref()
const input = ref()

// state
const state = reactive({
	dropdownOpen: false,
	inputValue: "",
})

// computed
const optionsToShow = computed(() => {
	return filterResults(state.inputValue)
})

// handlers
const displayDropdown = () => {
	if (props.disabled) return
	state.dropdownOpen = true
	state.inputValue = ""
	input.value.focus()
}
const hideDropdown = () => {
	state.dropdownOpen = false
	state.inputValue = props.selectedOption ? props.selectedOption.label : ""
}
const handleSelect = (option: SelectOption) => {
	state.dropdownOpen = false
	if (props.disabled) return
	state.inputValue = option.label
	emit("selectClick", option)
}
const handleInput = (e: any) => {
	state.inputValue = e.target.value
}

const filterResults = (value: string) => {
	if (!value) return props.options.slice(0, props.maxResults)

	const results: SelectOption[] = []
	for (const option of props.options) {
		if (results.length >= props.maxResults) break
		let matchFlag = false

		props.filterFields.forEach((field: string) => {
			const fieldKey = field as keyof SelectOption
			if (!option[fieldKey]) return
			if (
				option[fieldKey]
					.toString()
					.toLowerCase()
					.indexOf(value.toLowerCase()) !== -1
			)
				matchFlag = true
		})

		if (matchFlag) results.push(option)
	}
	return results
}

// click outside
onClickOutside(select, () => {
	if (state.dropdownOpen) hideDropdown()
})

watch(
	() => props.selectedOption,
	() => {
		state.inputValue = props.selectedOption ? props.selectedOption.label : ""
	}
)
</script>

<style  scoped>
.selectDropdown {
	position: absolute;
	width: 100%;
	bottom: -5px;
	left: 0;
	transform: translateY(100%);
	z-index: 50;
	padding: 16px;
	max-height: 250px;
	overflow-y: auto;
}

.form-control-plaintext:focus {
	border-color: none !important;
	box-shadow: none !important;
	outline: none !important;
}
</style>
